/**
 * useInventorySync Hook
 *
 * Purpose: Auto-sync inventory queue on app mount and network reconnect
 *
 * Features:
 *   - One-time legacy queue migration on mount
 *   - Auto-sync when app comes online
 *   - Last sync timestamp tracking
 *   - Network status monitoring
 *
 * Usage:
 *   const { isOnline, lastSyncTime, isSyncing } = useInventorySync();
 *
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { extendedInventoryService } from '@/services/inventory/inventoryServiceExtensions';
import { syncService } from '@/services/syncService';
import { eventBus, EVENT_TYPES } from '@/utils/eventBus';
import { logger } from '@/services/logger';

interface UseInventorySyncReturn {
  /** Whether the app is currently online */
  isOnline: boolean;
  /** Timestamp of last successful sync (null if never synced) */
  lastSyncTime: number | null;
  /** Whether sync is currently in progress */
  isSyncing: boolean;
  /** Number of items migrated from legacy queue (null if not migrated yet) */
  migratedItemsCount: number | null;
}

/**
 * Hook to handle automatic inventory synchronization
 *
 * This hook:
 * 1. Migrates legacy localStorage queue to IndexedDB (once)
 * 2. Syncs queue when app comes online
 * 3. Tracks sync status and last sync time
 */
export const useInventorySync = (): UseInventorySyncReturn => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [migratedItemsCount, setMigratedItemsCount] = useState<number | null>(null);

  // Refs to prevent duplicate operations
  const hasMigratedRef = useRef<boolean>(false);
  const isSyncingRef = useRef<boolean>(false);

  /**
   * Migrate legacy queue (one-time operation)
   */
  const migrateLegacyQueue = useCallback(async () => {
    // Prevent duplicate migration
    if (hasMigratedRef.current) {
      logger.debug('Legacy queue already migrated, skipping', undefined, 'useInventorySync');
      return;
    }

    try {
      logger.info('Starting legacy queue migration', undefined, 'useInventorySync');

      const migratedCount = await extendedInventoryService.migrateLegacyQueue();

      hasMigratedRef.current = true;
      setMigratedItemsCount(migratedCount);

      if (migratedCount > 0) {
        logger.info(
          `Migrated ${migratedCount} items from legacy queue`,
          undefined,
          'useInventorySync'
        );

        // If items were migrated and we're online, sync immediately
        if (navigator.onLine) {
          logger.info('Items migrated, triggering auto-sync', undefined, 'useInventorySync');
          await processQueueSafely();
        }
      } else {
        logger.debug('No items to migrate from legacy queue', undefined, 'useInventorySync');
      }
    } catch (error) {
      logger.error('Failed to migrate legacy queue', error, 'useInventorySync');
      // Don't throw - this is not critical
    }
  }, []);

  /**
   * Process queue (with duplicate prevention)
   */
  const processQueueSafely = useCallback(async () => {
    // Prevent concurrent syncs
    if (isSyncingRef.current) {
      logger.debug('Sync already in progress, skipping', undefined, 'useInventorySync');
      return;
    }

    try {
      isSyncingRef.current = true;

      logger.info('Processing queue', undefined, 'useInventorySync');

      const result = await syncService.processQueue();

      setLastSyncTime(Date.now());

      logger.info('Queue processed successfully', result, 'useInventorySync');
    } catch (error) {
      logger.error('Failed to process queue', error, 'useInventorySync');
    } finally {
      isSyncingRef.current = false;
    }
  }, []);

  /**
   * Handle online event
   */
  const handleOnline = useCallback(() => {
    logger.info('Network online, starting auto-sync', undefined, 'useInventorySync');
    setIsOnline(true);

    // Auto-sync when coming back online
    processQueueSafely();
  }, [processQueueSafely]);

  /**
   * Handle offline event
   */
  const handleOffline = useCallback(() => {
    logger.info('Network offline', undefined, 'useInventorySync');
    setIsOnline(false);
  }, []);

  /**
   * Mount effect: Migrate legacy queue and setup listeners
   */
  useEffect(() => {
    // Migrate legacy queue on mount (once)
    migrateLegacyQueue();

    // Setup network listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Subscribe to sync events (to update isSyncing state)
    const unsubscribeSyncStarted = eventBus.on(EVENT_TYPES.SYNC_STARTED, () => {
      setIsSyncing(true);
    });

    const unsubscribeSyncCompleted = eventBus.on(EVENT_TYPES.SYNC_COMPLETED, () => {
      setIsSyncing(false);
      setLastSyncTime(Date.now());
    });

    const unsubscribeSyncFailed = eventBus.on(EVENT_TYPES.SYNC_FAILED, () => {
      setIsSyncing(false);
    });

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribeSyncStarted();
      unsubscribeSyncCompleted();
      unsubscribeSyncFailed();
    };
  }, [migrateLegacyQueue, handleOnline, handleOffline]);

  return {
    isOnline,
    lastSyncTime,
    isSyncing,
    migratedItemsCount,
  };
};
